<template>
  <div>
    <!-- Product Header -->
    <div class="product-header">
      <div class="product-image-container">
        <app-img
          width="100%"
          height="100%"
          :img="item.img"
          class="product-image"
        />
      </div>

      <div class="product-header-info">
        <div class="product-name">{{ getProductName(item) }}</div>
        <div class="product-code">
          {{ $t("code") }}: <span>{{ item.code }}</span>
          <v-btn
            fab
            class="rounded-lg ml-2"
            depressed
            x-small
            @click="copyToClipboard(item.code)"
          >
            <v-icon small> fal fa-copy </v-icon>
          </v-btn>
        </div>
        <div class="product-category">
          <v-chip small color="primary" class="mr-2">
            {{
              $i18n.locale === "en"
                ? item.category?.en_name
                : item.category?.la_name
            }}
          </v-chip>
          <v-chip
            small
            :color="item.is_active ? 'success' : 'error'"
            dark
            class="mr-2"
          >
            <v-icon x-small left>
              {{
                item.is_active ? "fal fa-check-circle" : "fal fa-times-circle"
              }}
            </v-icon>
            {{ item.is_active ? $t("active") : $t("inactive") }}
          </v-chip>
        </div>
        <div class="price-tag" v-if="!item.has_variations">
          <span class="price-sale"
            >{{ item.price_sale | NumberFormat }} {{ currencyCode }}</span
          >
          <v-chip
            v-if="item.size"
            label
            dark
            color="green accent-3"
            small
            class="size-chip"
          >
            {{
              item.size.unit === "amount"
                ? $t("amount")
                : `${$t("pack")}|${item.size.amount}`
            }}
          </v-chip>
        </div>
      </div>
    </div>
    <v-row v-if="!item.has_variations">
      <v-col cols="12" md="6" v-if="item.track_stock">
        <v-card class="rounded-lg" outlined>
          <v-card-text>
            <div class="info-card-title">{{ $t("stock") }}</div>
            <div class="info-card-value">
              {{ item.stock | ToNumber }}
              <span class="caption">
                / {{ item.notify_stock | ToNumber }}
              </span>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="6">
        <v-card class="rounded-lg" outlined>
          <v-card-text>
            <div class="info-card-title">
              {{ $t("price_buy") }}
            </div>
            <div class="info-card-value">
              {{ item.price_buy | NumberFormat }} {{ currencyCode }}
              <span class="caption" v-if="item.exchange_rate > 1">
                / {{ original_buy_price }}
              </span>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="6">
        <v-card class="rounded-lg" outlined>
          <v-card-text>
            <div class="info-card-title">
              {{ $t("price_sale") }}
            </div>
            <div class="info-card-value">
              {{ item.price_sale | NumberFormat }} {{ currencyCode }}
              <span class="caption" v-if="item.exchange_rate > 1">
                / {{ original_sale_price }}
              </span>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="6">
        <v-card class="rounded-lg" outlined>
          <v-card-text>
            <div class="info-card-title">{{ $t("profit") }}</div>
            <div class="info-card-value">
              {{ profit | NumberFormat }} {{ currencyCode }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="6">
        <v-card class="rounded-lg" outlined>
          <v-card-text>
            <div class="info-card-title">{{ $t("total_sale") }}</div>
            <div class="info-card-value">
              {{ item.history_sum_total_price | NumberFormat }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Details Section -->
    <v-card class="rounded-lg mt-4" outlined>
      <v-card-title>{{ $t("product_details") }}</v-card-title>
      <v-card-text>
        <!-- category, unit, supplier, total_sold_qty, price_buy, price_sale, tax_amount, price_with_tax, cost_currency, cash_received, expired_date, created_at -->
        <!-- name_en, name_th, name_cn -->
        <div class="detail-row" v-if="item.name">
          <div class="detail-label"><LanguageFlag code="la">{{ $t("product_name") }}</LanguageFlag></div>
          <div class="detail-value">{{ item.name }}</div>
        </div>
        <div class="detail-row" v-if="item.name_en">
          <div class="detail-label"><LanguageFlag code="en">{{ $t("name_en") }}</LanguageFlag></div>
          <div class="detail-value">{{ item.name_en }}</div>
        </div>
        <div class="detail-row" v-if="item.name_th">
          <div class="detail-label"><LanguageFlag code="th">{{ $t("name_th") }}</LanguageFlag></div>
          <div class="detail-value">{{ item.name_th }}</div>
        </div>
        <div class="detail-row" v-if="item.name_cn">
          <div class="detail-label"><LanguageFlag code="cn">{{ $t("name_cn") }}</LanguageFlag></div>
          <div class="detail-value">{{ item.name_cn }}</div>
        </div>
        <div class="detail-row">
          <div class="detail-label">{{ $t("category") }}</div>
          <div class="detail-value">
            {{
              $i18n.locale === "en"
                ? item.category?.en_name
                : item.category?.la_name
            }}
          </div>
        </div>
        <div v-if="item.size" class="detail-row">
          <div class="detail-label">{{ $t("unit") }}</div>
          <div class="detail-value">{{ item.size.name }}</div>
        </div>
        <!-- Variant Information Section -->
        <div
          v-if="item.variant_info && item.variant_info.selected_options"
          class="detail-row"
        >
          <div class="detail-label">{{ $t("selected_variant") }}</div>
          <div class="detail-value">
            <div
              v-for="(value, key) in item.variant_info.selected_options"
              :key="key"
              class="variant-option"
            >
              <v-chip x-small color="primary" outlined class="mr-1 mb-1">
                {{ key }}: {{ value }}
              </v-chip>
            </div>
          </div>
        </div>

        <div
          v-if="item.variant_info && item.variant_info.variant"
          class="detail-row"
        >
          <div class="detail-label">{{ $t("variant_sku") }}</div>
          <div class="detail-value">{{ item.variant_info.variant.sku }}</div>
        </div>

        <div
          v-if="
            item.variant_info && item.variant_info.variant && item.track_stock
          "
          class="detail-row"
        >
          <div class="detail-label">{{ $t("variant_stock") }}</div>
          <div class="detail-value">
            {{ item.variant_info.variant.stock | NumberFormat }}
          </div>
        </div>

        <div class="detail-row">
          <div class="detail-label">{{ $t("supplier") }}</div>
          <div class="detail-value">{{ supplier }}</div>
        </div>

        <div class="detail-row" v-if="item.track_stock">
          <div class="detail-label">{{ $t("total_sold_qty") }}</div>
          <div class="detail-value">
            {{ item.history_sum_qty | ToNumber }}
          </div>
        </div>

        <template v-if="taxInclude">
          <div class="detail-row">
            <div class="detail-label">{{ $t("tax") }} {{ tax }}</div>
            <div class="detail-value">{{ item.tax_amount | NumberFormat }}</div>
          </div>

          <div class="detail-row">
            <div class="detail-label">{{ $t("price_with_tax") }}</div>
            <div class="detail-value">
              {{ item.price_sale | NumberFormat }} {{ currencyCode }}
            </div>
          </div>
        </template>
        <div class="detail-row" v-if="item.exchange_rate > 1">
          <div class="detail-label">{{ $t("cost_currency") }}</div>
          <div class="detail-value">
            {{ item.exchange_rate | NumberFormat }}/{{ currencyCode }}
          </div>
        </div>

        <div class="detail-row">
          <div class="detail-label">{{ $t("cash_received") }}</div>
          <div class="detail-value">
            {{
              (item.history_sum_total_price - item.history_sum_discount)
                | NumberFormat
            }}
          </div>
        </div>

        <div class="detail-row">
          <div class="detail-label">{{ $t("expired_date") }}</div>
          <div class="detail-value">
            {{ item?.expired_date ?? $t("none_expired") }}
          </div>
        </div>

        <div class="detail-row">
          <div class="detail-label">{{ $t("track_stock") }}</div>
          <div class="detail-value">
            <v-chip
              :color="item.track_stock ? 'success' : 'grey'"
              small
              outlined
            >
              {{ item.track_stock ? $t("enabled") : $t("disabled") }}
            </v-chip>
          </div>
        </div>

        <div class="detail-row">
          <div class="detail-label">{{ $t("status") }}</div>
          <div class="detail-value">
            <v-chip :color="item.is_active ? 'success' : 'error'" small dark>
              <v-icon x-small left>
                {{
                  item.is_active ? "fal fa-check-circle" : "fal fa-times-circle"
                }}
              </v-icon>
              {{ item.is_active ? $t("active") : $t("inactive") }}
            </v-chip>
          </div>
        </div>

        <div class="detail-row">
          <div class="detail-label">{{ $t("created_at") }}</div>
          <div class="detail-value">{{ item.created_at | moment }}</div>
        </div>
      </v-card-text>
    </v-card>

    <!-- Product Options Section -->
    <v-card v-if="hasOptions" class="rounded-lg mt-4" outlined>
      <v-card-title>
        {{ $t("options") }}
        <v-spacer />
        <v-chip small color="primary" class="mr-2">
          {{ item.options.length | ToNumber }}
        </v-chip>
      </v-card-title>
      <v-card-text>
        <div
          v-for="option in item.options"
          :key="option.name"
          class="option-row"
        >
          <span class="option-label">{{ option.name }}</span>
          <span class="option-values mt-1">
            <v-chip
              v-for="value in option.values"
              :key="value"
              small
              class="mr-1"
              color="primary lighten-3"
              >{{ value }}</v-chip
            >
          </span>
        </div>
      </v-card-text>
    </v-card>

    <!-- Product Variants Section -->
    <v-card v-if="hasVariants" class="rounded-lg mt-4" outlined>
      <v-card-title class="d-flex align-center">
        <v-icon left class="mr-2">fal fa-cubes</v-icon>
        {{ $t("variations") }}
        <v-spacer />
        <v-chip small color="primary" class="mr-2">
          {{ item.variants.length | ToNumber }}
        </v-chip>
      </v-card-title>
      <v-card-text class="pa-0">
        <v-expansion-panels>
          <v-expansion-panel
            v-for="(variant, index) in item.variants"
            :key="variant.sku"
            class="variant-panel"
          >
            <v-expansion-panel-header class="variant-header">
              <div class="d-flex align-center justify-space-between w-100">
                <div class="d-flex align-center">
                  <div class="mr-3">#{{ index + 1 }}</div>
                  <div>
                    <div class="variant-sku font-weight-medium">
                      {{ variant.sku }}
                    </div>
                    <div class="variant-options text-caption text--secondary">
                      <v-chip
                        v-for="(value, key) in variant.options"
                        :key="key"
                        x-small
                        outlined
                        class="mr-1"
                      >
                        {{ key }}: {{ value }}
                      </v-chip>
                    </div>
                  </div>
                </div>
                <div class="d-flex align-center">
                  <div class="text-right mr-4">
                    <div class="variant-price font-weight-bold">
                      {{ variant.price | NumberFormat }}
                      {{ currencyCode }}
                    </div>
                    <div class="variant-cost text-caption text--secondary">
                      {{ $t("cost") }}: {{ variant.cost | NumberFormat }}
                    </div>
                  </div>
                </div>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content class="variant-content">
              <v-row>
                <v-col cols="12">
                  <div class="variant-options-section">
                    <h4 class="variant-detail-title">
                      {{ $t("variant_options") }}
                    </h4>
                    <div class="variant-options-grid">
                      <div
                        v-for="(value, key) in variant.options"
                        :key="key"
                        class="option-item"
                      >
                        <v-chip color="primary" outlined class="option-chip">
                          <span class="option-name">{{ key }}:</span>
                          <span class="option-value ml-1">{{ value }}</span>
                        </v-chip>
                      </div>
                    </div>
                  </div>
                </v-col>
                <v-col cols="12">
                  <div class="variant-detail-section">
                    <h4 class="variant-detail-title">
                      {{ $t("variant_details") }}
                    </h4>
                    <div class="variant-detail-grid">
                      <div class="detail-item">
                        <span class="detail-label">{{ $t("sku") }}:</span>
                        <span class="detail-value">{{ variant.sku }}</span>
                      </div>
                      <div class="detail-item">
                        <span class="detail-label">{{ $t("barcode") }}:</span>
                        <span class="detail-value">
                          {{ variant.barcode }}
                          <v-btn
                            icon
                            small
                            class="ml-2"
                            @click="copyToClipboard(variant.barcode)"
                          >
                            <v-icon small>fal fa-copy</v-icon>
                          </v-btn>
                        </span>
                      </div>
                      <div class="detail-item">
                        <span class="detail-label">{{ $t("cost") }}:</span>
                        <span class="detail-value">{{
                          variant.cost | NumberFormat
                        }}</span>
                      </div>
                      <div class="detail-item">
                        <span class="detail-label">{{ $t("price") }}:</span>
                        <span class="detail-value font-weight-bold">{{
                          variant.price | NumberFormat
                        }}</span>
                      </div>
                      <div class="detail-item" v-if="item.track_stock">
                        <span class="detail-label">{{ $t("stock") }}:</span>
                        <span class="detail-value">
                          <v-chip
                            :color="
                              getVariantStockColor(
                                variant.stock,
                                item.track_stock
                              )
                            "
                            small
                            dark
                          >
                            {{
                              getVariantStockDisplay(
                                variant.stock,
                                item.track_stock
                              )
                            }}
                          </v-chip>
                        </span>
                      </div>
                      <div class="detail-item" v-if="item.track_stock">
                        <span class="detail-label">{{ $t("profit") }}:</span>
                        <span class="detail-value">
                          <v-chip
                            :color="
                              getVariantProfitColor(variant.price, variant.cost)
                            "
                            small
                            dark
                          >
                            {{
                              getVariantProfit(variant.price, variant.cost)
                                | NumberFormat
                            }}
                          </v-chip>
                        </span>
                      </div>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card-text>
    </v-card>

    <!-- Bundle Items Section -->
    <v-card v-if="hasBundleItems" class="rounded-lg mt-4" outlined>
      <v-card-title class="d-flex align-center">
        <v-icon left class="mr-2">fal fa-boxes</v-icon>
        {{ $t("bundle_products") }}
        <v-spacer />
        <v-chip small color="primary" class="mr-2">
          {{ item.bundle_items.length | ToNumber }}
        </v-chip>
      </v-card-title>
      <v-card-text>
        <v-simple-table>
          <thead>
            <tr>
              <th>{{ $t("product") }}</th>
              <th>{{ $t("price_sale") }}</th>
              <th style="width: 120px">{{ $t("qty") }}</th>
              <th style="width: 160px">{{ $t("stock") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="bi in item.bundle_items" :key="bi.product_id">
              <td>
                <div class="d-flex align-center">
                  <v-avatar size="36" class="mr-3">
                    <app-img
                      :img="bi.product?.img || '/images/no_photo.jpg'"
                      width="36"
                      height="36"
                    />
                  </v-avatar>
                  <div>
                    <div class="font-weight-medium">{{ bi.product?.name }}</div>
                    <div class="text-caption text--secondary">
                      {{ bi.product?.code }}
                    </div>
                  </div>
                </div>
              </td>
              <td>{{ bi.product?.price_sale | NumberFormat }}</td>
              <td>
                <v-chip small outlined color="info">{{
                  bi.quantity | ToNumber
                }}</v-chip>
              </td>
              <td>
                <v-chip
                  small
                  :color="
                    bi.product?.track_stock === 0 ||
                    bi.product?.track_stock === '0'
                      ? 'info'
                      : (bi.product?.stock || 0) > 0
                      ? 'success'
                      : 'error'
                  "
                  dark
                >
                  <span
                    v-if="
                      bi.product?.track_stock === 0 ||
                      bi.product?.track_stock === '0'
                    "
                    >{{ $t("unlimited") }}</span
                  >
                  <span v-else>{{ bi.product?.stock | ToNumber }}</span>
                </v-chip>
              </td>
            </tr>
          </tbody>
        </v-simple-table>
      </v-card-text>
    </v-card>

    <!-- No Options/Variants Message -->
    <v-card v-if="!hasOptions && !hasVariants" class="rounded-lg mt-4" outlined>
      <v-card-text>
        <em> {{ $t("no_options_or_variants") }} </em>
      </v-card-text>
    </v-card>

    <!-- Product Variations Section -->
    <ProductVariationDisplay
      v-if="item.has_variations && item.variations"
      :product="item"
      :read-only="true"
    />
  </div>
</template>

<script>
import { myMixin } from "@/mixin/main";
import ProductVariationDisplay from "./ProductVariationDisplay.vue";

export default {
  mixins: [myMixin],
  components: {
    ProductVariationDisplay,
  },
  props: {
    item: {
      type: Object,
    },
  },
  computed: {
    currencyCode() {
      return this.item?.currency?.code || "LAK";
    },
    supplier() {
      if (
        this.item.vendor &&
        this.item.vendor.name &&
        this.item.vendor.phone_no
      ) {
        return this.item.vendor.name + " - " + this.item.vendor.phone_no;
      } else if (this.item.vendor && this.item.vendor.name) {
        return this.item.vendor.name;
      } else if (this.item.vendor && this.item.vendor.phone_no) {
        return this.item.vendor.phone_no;
      } else {
        return "";
      }
    },

    profit() {
      const buy = this.item.price_buy;
      const sale = this.item.price_sale;
      const tax = this.item.tax_amount;
      const a = sale - tax;
      return a - buy;
    },
    original_buy_price() {
      // Since backend now stores original prices, no conversion needed
      const total = this.item.price_buy;
      const code = this.currencyCode;
      return `${this.$options.filters.NumberFormat(total)} ${code}`;
    },
    original_sale_price() {
      // Since backend now stores original prices, no conversion needed
      const total = this.item.price_sale;
      const code = this.currencyCode;
      return `${this.$options.filters.NumberFormat(total)} ${code}`;
    },
    hasOptions() {
      return Array.isArray(this.item.options) && this.item.options.length > 0;
    },
    hasVariants() {
      return Array.isArray(this.item.variants) && this.item.variants.length > 0;
    },
    hasBundleItems() {
      return (
        this.item &&
        this.item.is_bundle &&
        Array.isArray(this.item.bundle_items) &&
        this.item.bundle_items.length > 0
      );
    },
  },
  methods: {
    getVariantStockColor(stock, trackStock) {
      // If track_stock is 0, show different color (don't track stock)
      if (trackStock === 0) return "info";

      // For products that track stock
      if (stock <= 0) return "error";
      if (stock <= 5) return "warning";
      return "success";
    },

    getVariantStockDisplay(stock, trackStock) {
      // If track_stock is 0, show "N/A" or similar
      if (trackStock === 0) return this.$t("no_track_stock") || "N/A";

      // For products that track stock, show the actual stock number
      return this.$options.filters.ToNumber(stock);
    },

    getVariantProfit(price, cost) {
      return price - cost;
    },

    getVariantProfitColor(price, cost) {
      const profit = price - cost;
      const profitMargin = (profit / cost) * 100;

      if (profitMargin >= 50) return "success";
      if (profitMargin >= 20) return "warning";
      return "error";
    },
  },
};
</script>

<style scoped>
.product-header {
  display: flex;
  margin-bottom: 32px;
  gap: 32px;
  align-items: center;
}

.product-image-container {
  width: 200px;
  height: 200px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
  background-color: white;
}

.product-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.product-header-info {
  flex: 1;
}

.product-name {
  font-size: 28px;
  font-weight: 600;
  margin-bottom: 8px;
}

.product-code {
  font-size: 14px;
  margin-bottom: 8px;
}

.product-code span {
  font-weight: 600;
}

.product-category {
  display: inline-block;
  margin-bottom: 16px;
  font-size: 14px;
  padding: 4px 12px;
  border-radius: 20px;
  font-weight: 500;
}

.price-tag {
  display: flex;
  align-items: center;
  gap: 12px;
}

.price-sale {
  font-size: 28px;
  font-weight: 700;
  color: #2c64c6;
}

.size-chip {
  margin-left: 8px;
}

.info-card-title {
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
}

.info-card-value {
  font-size: 1.25rem;
  font-weight: 600;
}

.cursor-help {
  cursor: help;
}

/* Weighted Average Tooltip Styles */
.weighted-average-tooltip {
  max-width: 300px;
  line-height: 1.4;
}

.weighted-average-tooltip .text-caption {
  font-size: 0.75rem;
  line-height: 1.3;
}

.weighted-average-tooltip strong {
  color: #1976d2;
}

.detail-row {
  display: flex;
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid #eee;
}

.detail-row:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.detail-label {
  width: 180px;
  font-size: 15px;
}

.detail-value {
  flex: 1;
  font-weight: 500;
}

.option-row {
  margin-bottom: 8px;
}

.option-label {
  font-weight: 600;
  margin-right: 8px;
}

.option-values {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

/* Variant Panel Styles */
.variant-panel {
  border-bottom: 1px solid #eee;
}

.variant-panel:last-child {
  border-bottom: none;
}

.variant-header {
  padding: 16px 20px;
}

.variant-sku {
  font-size: 14px;
  margin-bottom: 4px;
}

.variant-options {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.variant-price {
  font-size: 16px;
  color: #2c64c6;
}

.variant-cost {
  font-size: 12px;
}

.variant-content {
  padding: 20px;
  background-color: #f8f9fa;
}

.variant-detail-section,
.variant-options-section {
  background: white;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
}

.variant-detail-title {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 12px;
  color: #333;
}

.variant-detail-grid {
  display: grid;
  gap: 8px;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
}

.detail-label {
  font-size: 13px;
  color: #666;
  font-weight: 500;
}

.detail-value {
  font-size: 13px;
  font-weight: 600;
  color: #333;
}

.variant-options-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.option-item {
  margin-bottom: 8px;
}

.option-chip {
  font-size: 12px;
}

.option-name {
  font-weight: 600;
}

.option-value {
  font-weight: 500;
}

/* -------------------------- Dark mode overrides -------------------------- */
/* Vuetify applies theme classes on parents; use ::v-deep to reach through scoped styles. */
::v-deep .theme--dark .detail-row {
  border-bottom-color: rgba(255, 255, 255, 0.12);
}

::v-deep .theme--dark .variant-panel {
  border-bottom-color: rgba(255, 255, 255, 0.12);
}

::v-deep .theme--dark .variant-content {
  background-color: rgba(255, 255, 255, 0.06);
}

::v-deep .theme--dark .variant-detail-section,
::v-deep .theme--dark .variant-options-section {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.12);
}

::v-deep .theme--dark .variant-detail-title,
::v-deep .theme--dark .detail-value,
::v-deep .theme--dark .product-name,
::v-deep .theme--dark .product-code,
::v-deep .theme--dark .info-card-value {
  color: rgba(255, 255, 255, 0.87);
}

::v-deep .theme--dark .detail-label,
::v-deep .theme--dark .variant-cost,
::v-deep .theme--dark .caption {
  color: rgba(255, 255, 255, 0.6);
}

::v-deep .theme--dark .variant-price {
  color: #90caf9;
}

::v-deep .theme--dark .weighted-average-tooltip strong {
  color: #90caf9;
}

@media (max-width: 768px) {
  .product-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .product-image-container {
    width: 100%;
    height: 250px;
    margin-bottom: 24px;
  }

  .detail-row {
    flex-direction: column;
  }

  .detail-label {
    width: 100%;
    margin-bottom: 4px;
  }
}
</style>
